import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { ADDENDUM_FIELDS } from '@/lib/addendumFields'

export interface ServiceDeltasListProps {
  /** Values as the service was locked. */
  lockedValues: Record<string, string | undefined>
  /** Values in force now, with every addendum applied. */
  currentValues: Record<string, string | undefined>
  statusLabel: string
}

/**
 * Everything that differs from the locked snapshot, in one place.
 *
 * The record itself keeps showing plain current values; this is the opt-in
 * audit lens behind the "show changes since …" toggle.
 */
export function ServiceDeltasList(props: ServiceDeltasListProps) {
  const { lockedValues, currentValues, statusLabel } = props

  const deltas = ADDENDUM_FIELDS.flatMap((entry) => {
    const before = lockedValues[entry.field] ?? ''
    const after = currentValues[entry.field] ?? ''
    if (before === after) return []
    return [{ ...entry, before, after }]
  })

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
      <FormCapsLabel>Changes since {statusLabel.toLowerCase()}</FormCapsLabel>
      {deltas.length === 0 ? (
        <p className="text-xs text-slate-500">
          Nothing has changed since this record was{' '}
          {statusLabel.toLowerCase()}.
        </p>
      ) : (
        <dl className="divide-y divide-slate-100">
          {deltas.map((delta) => (
            <div
              key={delta.field}
              className="grid grid-cols-[10rem_minmax(0,1fr)] items-start gap-2 bg-emerald-50/50 py-1.5"
            >
              <dt className="pt-0.5 text-xs text-slate-500">{delta.label}</dt>
              <dd className="text-sm whitespace-pre-wrap text-slate-900">
                <span className="text-rose-700/80 line-through decoration-rose-400">
                  {delta.before === '' ? '(empty)' : delta.before}
                </span>{' '}
                <span className="text-slate-400">→</span>{' '}
                <span className="font-medium text-emerald-800">
                  {delta.after === '' ? '(cleared)' : delta.after}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
